import React, { createContext, useContext, useMemo } from "react";
import { Box, Checkbox, Tooltip } from "@mui/material";
import { type BulkPropertyRow } from "../types";
import { type AcquirePolicy } from "../utils/acquirePolicy";
import {
  useCheckedField,
  type CheckedIdsStore,
} from "../hooks/useCheckedIdsStore";
import { getBulkCellDisabledReason } from "../utils/cellAvailability";
import { isReuseCheckboxDisabled } from "../utils/reuseAvailability";
import { buildReuseTooltipLines } from "../utils/reuseTooltip";
import { getCellReuseMarks, isReuseUsableNow } from "../utils/reuseMarks";
import { AcquiredMark, ReusableDot } from "./BulkMarks";

interface BulkCheckboxCellContextValue {
  store: CheckedIdsStore;
  policy: AcquirePolicy;
  pastDays: number;
  /** 自分が取得済みの登記をチェックさせない指定 */
  excludeAcquired: boolean;
  /** 経過日数の判定基準時刻。条件が変わるたびに画面側で作り直す */
  now: Date;
  onToggle: (field: string, row: BulkPropertyRow) => void;
}

export const BulkCheckboxCellContext =
  createContext<BulkCheckboxCellContextValue | null>(null);

/**
 * チェックセルが必要とするストア・取得条件・操作を配る。
 * セル側が条件を購読することで列定義を静的にでき、条件変更時も可視セルだけが再評価される。
 */
export const BulkCheckboxCellProvider: React.FC<{
  value: BulkCheckboxCellContextValue;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <BulkCheckboxCellContext.Provider value={value}>
    {children}
  </BulkCheckboxCellContext.Provider>
);

/** チェックセル。自列の Set のみ購読するため、他列のチェック操作では再描画されない */
export const BulkCheckboxCell: React.FC<{
  field: string;
  headerName: string;
  row: BulkPropertyRow;
}> = React.memo(({ field, headerName, row }) => {
  const context = useContext(BulkCheckboxCellContext);
  if (context == null) {
    throw new Error(
      "BulkCheckboxCell は BulkCheckboxCellProvider の内側で使う"
    );
  }
  const { store, policy, pastDays, excludeAcquired, now, onToggle } = context;
  const ids = useCheckedField(store, field);
  // 閉鎖チェックは他列の可否を左右するため、排他対象列は閉鎖の集合も購読する
  const closureCheckedIds = useCheckedField(store, "requestClosureBook");
  // チェックの ON/OFF だけが変わる再描画で、不可判定・ツールチップ・印の組み立てを繰り返さない
  const { disabled, tooltip, usableNow, marks } = useMemo(() => {
    const reason = getBulkCellDisabledReason(
      field,
      row,
      closureCheckedIds,
      excludeAcquired
    );
    return {
      disabled:
        reason != null ||
        isReuseCheckboxDisabled(
          field,
          row,
          policy,
          pastDays,
          now,
          excludeAcquired
        ),
      // 不可の理由を先頭に置き、候補一覧を続ける（候補由来の不可は一覧の各行が理由を持つ）
      tooltip: [
        reason,
        ...buildReuseTooltipLines(
          field,
          row,
          policy,
          pastDays,
          now,
          excludeAcquired
        ),
      ]
        .filter((line) => line != null)
        .join("\n"),
      usableNow: isReuseUsableNow(field, row, policy, pastDays, now),
      marks: getCellReuseMarks(field, row),
    };
  }, [field, row, closureCheckedIds, excludeAcquired, policy, pastDays, now]);
  return (
    <Tooltip
      title={tooltip}
      // 既定のツールチップは自身がクリックを受け取るため、真下に重なった行の
      // チェックボックスを押せなくなる。クリックを透過させる
      disableInteractive
      componentsProps={{
        tooltip: { sx: { whiteSpace: "pre-line" } },
      }}
    >
      {/* 非活性のチェックボックスはイベントを発火しないため span で受ける。
          印はこの span を基準に置く（中心がチェックボックスと一致する） */}
      {/* 非活性時のみフォーカス可能にし、Tooltip の focus ハンドラで理由を読み上げさせる */}
      <Box
        component="span"
        tabIndex={disabled ? 0 : undefined}
        sx={{ position: "relative", display: "inline-flex" }}
      >
        <Checkbox
          size="small"
          checked={ids.has(row.id)}
          disabled={disabled}
          onChange={() => {
            onToggle(field, row);
          }}
          inputProps={{
            "aria-label": `${headerName} No${row.id} ${row.location}`,
          }}
        />
        {marks.acquired && <AcquiredMark />}
        {marks.reusable && <ReusableDot stale={!usableNow} />}
      </Box>
    </Tooltip>
  );
});
BulkCheckboxCell.displayName = "BulkCheckboxCell";
